import React from 'react';
// NOTE:
// We intentionally keep markdown rendering lightweight on the client, and do NOT run
// markdown rendering in the SSR bundle (it can cause TDZ/circular-init crashes).
// For SSR-critical blog posts we now prefer `content_html` computed by the API server.

/**
 * Renders markdown text as React components
 *
 * @param {string} text - The markdown text to render
 * @param {Object} options - Options for rendering
 * @param {Object} options.components - (legacy, ignored) custom components mapping
 * @returns {React.Element} The rendered markdown as React components
 */
export const renderMarkdown = (text, options = {}) => {
  if (typeof text !== 'string') {
    return text;
  }
  const { components = {} } = options;

  try {
    // SSR-safe fallback: don't evaluate markdown libs on the server.
    if (typeof window === 'undefined') {
      return React.createElement(React.Fragment, null, text);
    }

    // Browser-only markdown rendering.
    // eslint-disable-next-line global-require
    const ReactMarkdown = require('react-markdown').default;
    // eslint-disable-next-line global-require
    const remarkGfm = require('remark-gfm').default;

    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    );
  } catch (error) {
    return text;
  }
};

/**
 * Renders markdown text with custom styling classes
 *
 * @param {string} text - The markdown text to render
 * @param {Object} options - Options for rendering
 * @param {string} options.headingClassName - CSS class for headings
 * @param {string} options.paragraphClassName - CSS class for paragraphs
 * @param {string} options.linkClassName - CSS class for links
 * @param {string} options.listClassName - CSS class for lists
 * @param {string} options.listItemClassName - CSS class for list items
 * @param {string} options.blockquoteClassName - CSS class for blockquotes
 * @returns {React.Element} The rendered markdown as React components
 */
export const renderMarkdownWithStyles = (text, options = {}) => {
  const {
    headingClassName,
    paragraphClassName,
    linkClassName,
    listClassName,
    listItemClassName,
    blockquoteClassName
  } = options;

  const components = {};

  if (headingClassName) {
    components.h1 = props => <h1 {...props} className={headingClassName} />;
    components.h2 = props => <h2 {...props} className={headingClassName} />;
    components.h3 = props => <h3 {...props} className={headingClassName} />;
    components.h4 = props => <h4 {...props} className={headingClassName} />;
    components.h5 = props => <h5 {...props} className={headingClassName} />;
    components.h6 = props => <h6 {...props} className={headingClassName} />;
  }

  if (paragraphClassName) {
    components.p = props => <p {...props} className={paragraphClassName} />;
  }

  if (linkClassName) {
    components.a = props => (
      <a {...props} className={linkClassName} target="_blank" rel="noopener noreferrer" />
    );
  }

  if (listClassName) {
    components.ul = props => <ul {...props} className={listClassName} />;
    components.ol = props => <ol {...props} className={listClassName} />;
  }
  if (listItemClassName) {
    components.li = props => <li {...props} className={listItemClassName} />;
  }

  if (blockquoteClassName) {
    components.blockquote = props => <blockquote {...props} className={blockquoteClassName} />;
  }

  return renderMarkdown(text, { components });
};
